import React, { useState, useCallback, useEffect } from 'react';
import { createTheme, ThemeProvider, Container, Grid, Paper, Typography, Collapse, IconButton } from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import { ListeDemandes } from './ListesDemandes';
import InfoVoyageur from './InfoVoyageur';
import InfoFormulaire from './InfoFormulaire';
import SuiviClientDossier from './SuiviClientDossier';
import Home from './Home';
import FacturationDialog from './FacturationDialog';

// Créez un thème personnalisé avec les réglages souhaités pour le container
const theme = createTheme({
  components: {
    MuiContainer: {
      styleOverrides: {
        maxWidthLg: {
          '@media (min-width: 1200px)': {
            maxWidth: '2400px',
          },
        },
      },
    },
  },
});

// Définition du type pour gérer la sélection 
type SelectionType = {
  idVoyageur: number | null;
  chemin: string | null;
  pays: string | null;
  idDemande: number | null;
  email: string | null; // mail du compte client, affiché dans Informations du formulaire
  source: string | null; // demandes.source ('externe_inde_tourisme' : dossier du site Inde tourisme)
};

const DemandesScreen: React.FC = () => {
  // Initialisation de la sélection
  const [selection, setSelection] = useState<SelectionType>({
    idVoyageur: null,
    chemin: null,
    pays: null,
    idDemande: null,
    email: null,
    source: null,
  });

  const [key, setKey] = useState<number>(0); // Clé pour forcer le rechargement du composant ListeDemandes
  const [user, setUser] = useState<any>(null);

  // États pour gérer l'expansion/collapse des sections
  const [isDemandesExpanded, setIsDemandesExpanded] = useState<boolean>(true);
  const [isVoyageurExpanded, setIsVoyageurExpanded] = useState<boolean>(true);
  const [isFormulaireExpanded, setIsFormulaireExpanded] = useState<boolean>(true);
  const [isTableCollapsed, setIsTableCollapsed] = useState<boolean>(false);

  // États pour le dialog de facturation
  const [facturationDialogOpen, setFacturationDialogOpen] = useState<boolean>(false);
  const [selectedDemandeIdForFacturation, setSelectedDemandeIdForFacturation] = useState<number | null>(null);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
  }, []);

  /**
   * Fonction appelée lorsque l'on clique sur une ligne dans ListeDemandes
   * @param idVoyageur L'identifiant du voyageur
   * @param chemin     Le chemin (dossier) associé à la demande
   * @param pays       Le libellé de la colonne Pays (demande.titre)
   * @param idDemande  L'identifiant de la demande
   * @param email      Le mail du compte client de la demande
   * @param source     L'origine de la demande (demandes.source)
   */
  const handleSelectionDemande = (idVoyageur: number, chemin: string, pays: string, idDemande?: number, email?: string, source?: string) => {
    setSelection({ idVoyageur, chemin, pays, idDemande: idDemande || null, email: email || null, source: source || null });
  };

  // Fonction pour rafraîchir la liste des demandes
  const rafraichirDemandes = useCallback(() => {
    setKey((prevKey) => prevKey + 1);
  }, []);

  // Fonctions pour basculer l'expansion des sections
  const toggleDemandesExpansion = () => {
    setIsDemandesExpanded((prev) => !prev);
  };

  const toggleVoyageurExpansion = () => {
    setIsVoyageurExpanded((prev) => !prev);
  };

  const toggleFormulaireExpansion = () => {
    setIsFormulaireExpanded((prev) => !prev);
  };

  // Fonction pour gérer le changement d'état collapsed du tableau
  const handleCollapsedChange = (isCollapsed: boolean) => {
    setIsTableCollapsed(isCollapsed);
    if (isCollapsed && user?.role === 'admin') {
      // Si on collapse, on affiche automatiquement les sections voyageur et formulaire
      setIsVoyageurExpanded(true);
      setIsFormulaireExpanded(true);
    }
  };

  // Fonction pour ouvrir le dialog de facturation
  const handleFacturationClick = (demandeId: number) => {
    setSelectedDemandeIdForFacturation(demandeId);
    setFacturationDialogOpen(true);
  };

  // Fonction pour fermer le dialog de facturation
  const handleCloseFacturationDialog = () => {
    setFacturationDialogOpen(false);
    setSelectedDemandeIdForFacturation(null);
  };

  // Fonction appelée après succès de facturation
  const handleFacturationSuccess = () => {
    rafraichirDemandes();
  };

  return (
    <ThemeProvider theme={theme}>
      <Home />

      <Container sx={{ pt: 2 }}>
        <Grid container spacing={3}>
          {/* Liste des demandes avec possibilité de collapse */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <IconButton onClick={toggleDemandesExpansion}>
                  {isDemandesExpanded ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </div>
              <Collapse in={isDemandesExpanded}>
                <ListeDemandes
                  onSelectionDemande={handleSelectionDemande}
                  onCollapsedChange={handleCollapsedChange}
                  onFacturationClick={handleFacturationClick}
                  key={key}
                />
              </Collapse>
            </Paper>
          </Grid>

          {/* Informations du Voyageur et Formulaire - Afficher si voyageur sélectionné */}
          {selection.idVoyageur && (
            <Grid container item xs={12} spacing={3}>
              <Grid item xs={12} md={6} lg={6}>
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">Informations du Voyageur</Typography>
                    <IconButton onClick={toggleVoyageurExpansion}>
                      {isVoyageurExpanded ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                  </div>
                  <Collapse in={isVoyageurExpanded}>
                    <InfoVoyageur
                      key={selection.idVoyageur ?? 0}
                      idVoyageur={selection.idVoyageur ?? 0}
                      chemin={selection.chemin ?? ""}
                      pays={selection.pays ?? ""}
                      idDemande={selection.idDemande ?? 0}
                      source={selection.source ?? undefined}
                      onValider={rafraichirDemandes}
                    />
                  </Collapse>
                </Paper>
              </Grid>

              {/* Informations du Formulaire */}
              <Grid item xs={12} md={6} lg={6}>
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">Informations du Formulaire</Typography>
                    <IconButton onClick={toggleFormulaireExpansion}>
                      {isFormulaireExpanded ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                  </div>
                  <Collapse in={isFormulaireExpanded}>
                    <InfoFormulaire chemin={selection.chemin ?? ""} emailClient={selection.email} />
                  </Collapse>
                  {/* Suivi client des dossiers du site Inde tourisme (rien pour les autres dossiers) : hors du repli,
                      il reste visible quand les détails du formulaire sont repliés ; relu après chaque action (key) */}
                  <SuiviClientDossier idDemande={selection.idDemande} actualisation={key} />
                </Paper>
              </Grid>
            </Grid>
          )}
        </Grid>
      </Container>

      {/* Dialog de facturation */}
      {selectedDemandeIdForFacturation && (
        <FacturationDialog
          open={facturationDialogOpen}
          onClose={handleCloseFacturationDialog}
          demandeId={selectedDemandeIdForFacturation}
          onSuccess={handleFacturationSuccess}
        />
      )}
    </ThemeProvider>
  );
};

export default DemandesScreen;
